import { QUIZ_ROUTES } from "./quizRoutes";
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  buildQuizBackTarget,
  buildQuizFlowState,
  persistQuizFlowState,
} from "./quizFlowState";
import "./TestSeriesCard.css";

import shareIcon from "../../assets/images/sharee.png";
import avatar1 from "../../assets/images/bg3.png";
import avatar2 from "../../assets/images/bg2.png";
import avatar3 from "../../assets/images/bg1.png";

const defaultAvatars = [avatar1, avatar2, avatar3];

const TestSeriesCard = ({
  items = [],
  isLoading = false,
  emptyMessage = "No items available.",
  navigationState = {},
}) => {
  const navigate = useNavigate();

  const handleNavigate = (test) => {
    const seriesId = Number(test?.id || test?.series_id || 0);
    const seriesTitle = test?.title || "Test Series";
    const seriesState = buildQuizFlowState({
      ...navigationState,
      route_key: 2,
      selectedQuizOption: navigationState?.selectedQuizOption || "Test Series",
      series_id: seriesId,
      seriesName: seriesTitle,
      selectedSeries: {
        id: seriesId,
        series_id: seriesId,
        title: seriesTitle,
        name: seriesTitle,
      },
      quizListPath: QUIZ_ROUTES.testSeriesList,
      quizBackTarget: buildQuizBackTarget(QUIZ_ROUTES.list, navigationState),
    });

    persistQuizFlowState(seriesState);
    navigate(QUIZ_ROUTES.testSeriesList, {
      state: seriesState,
    });
  };

  if (isLoading) {
    return (
      <div className="test-series-wrapper">
        {Array.from({ length: 4 }).map((_, index) => (
          <div className="test-series-card" key={`test-series-skeleton-${index}`}>
            <div className="test-series-top">
              <div className="dashboard-skeleton dashboard-skeleton--thumb" style={{ width: 40, height: 40, borderRadius: 9 }} />
              <div className="test-series-title-wrap">
                <div className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title" />
              </div>
              <div className="dashboard-skeleton dashboard-skeleton--icon" style={{ width: 25, height: 25 }} />
            </div>
            <div className="dashboard-skeleton dashboard-skeleton--line mt-3" />
            <div className="dashboard-skeleton dashboard-skeleton--line mt-2" />
            <div className="dashboard-skeleton dashboard-skeleton--line mt-2" />
            <div className="dashboard-skeleton dashboard-skeleton--line mt-3" />
          </div>
        ))}
      </div>
    );
  }

  if (!items.length) {
    return <p className="choose-state__status">{emptyMessage}</p>;
  }

  return (
    <div className="test-series-wrapper">
      {items.map((test, index) => (
        <div
          className="test-series-card test-series-card--interactive"
          key={test.id || test.number || index}
          onClick={() => handleNavigate(test)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleNavigate(test);
            }
          }}
          role="button"
          tabIndex={0}
        >
          {(() => {
            const examAvatars = Array.isArray(test.mdExams)
              ? test.mdExams
                  .map((exam) => exam?.icon_url)
                  .filter(Boolean)
                  .slice(0, 3)
              : [];
            const resolvedAvatars = examAvatars.length ? examAvatars : defaultAvatars;
            const examCount = Number(test.examCount || test.mdExams?.length || resolvedAvatars.length || 0);

            return (
              <>
                <div className="test-series-top">
                  <div className="test-series-number">{test.number || String(index + 1).padStart(2, "0")}</div>

                  <div className="test-series-title-wrap">
                    <h3>{test.title}</h3>
                  </div>

                  <button
                    type="button"
                    className="test-series-share"
                    aria-label={`Share ${test.title}`}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <img src={shareIcon} alt="" />
                  </button>
                </div>

                <p className="test-series-description">
                  {test.description || "Practice with comprehensive test series to prepare for exams."}
                </p>

                <div className="test-series-language">
                  <i className="bi bi-translate"></i>
                  <span>{test.language || "English"}</span>
                </div>

                <div className="test-series-date-row">
                  <div className="test-series-date">
                    <i className="bi bi-calendar3"></i>
                    <span>{test.date || "TBA"}</span>
                  </div>

                  <div className="test-series-users">
                    <span className="test-series-count">+{examCount}</span>

                    <div className="test-series-avatars">
                      {resolvedAvatars.map((avatar, avatarIndex) => (
                        <img
                          key={`${test.id || test.number || index}-avatar-${avatarIndex}`}
                          src={avatar}
                          alt=""
                          className="test-avatar"
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="test-series-progress">
                  <div className="test-series-progress-line">
                    <div
                      className="test-series-progress-value"
                      style={{
                        width: `${Math.min(Math.max(test.progress || 0, 0), 100)}%`,
                      }}
                    ></div>
                  </div>
                </div>

                <div className="test-series-stats">
                  <div className="test-series-stat">
                    <div className="test-series-stat-icon">
                      <i className="bi bi-gift"></i>
                    </div>

                    <div>
                      <strong className="green-text">{test.freeCount || (test.plan === "Free" ? "1" : "0")}</strong>
                      <span className="smallText">Free</span>
                    </div>
                  </div>

                  <div className="test-series-stat">
                    <div className="test-series-stat-icon">
                      <i className="bi bi-calendar3"></i>
                    </div>

                    <div>
                      <strong className="green-text">{test.questions || "0"}</strong>
                      <span className="smallText">Tests</span>
                    </div>
                  </div>

                  <div className="test-series-stat">
                    <div className="test-series-stat-icon">
                      <i className="bi bi-check-circle"></i>
                    </div>

                    <div>
                      <strong className="blue-text">{test.attempts || "0"}</strong>
                      <span className="smallText">Attempts</span>
                    </div>
                  </div>

                  <div className="test-series-stat">
                    <div className="test-series-stat-icon">
                      <i className="bi bi-arrow-counterclockwise"></i>
                    </div>

                    <div>
                      <strong className="orange-text">{test.attemptsLeft || test.marks || "0"}</strong>
                      <span className="smallText">Attempts Left</span>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      ))}
    </div>
  );
};

export default TestSeriesCard;
